/**
 * The runtime portrait compositor.
 *
 * A captain is a seed. This turns the appearance half of that seed into pixels, once,
 * and hands back a buffer somebody else blits. It is the only file in `src/captain/`
 * that the UI has to know about.
 *
 * Four things it guarantees, in the order they matter:
 *
 *  1. **Same seed, same face, forever.** `paintCaptain` is a pure function of the
 *     appearance genes and touches no clock, no `Math.random` and no DOM. Two runs of
 *     the same share string produce byte-identical portraits.
 *  2. **The face belongs to the arena.** The composited image goes through `crt.ts`, a
 *     line-for-line mirror of the shipped post chain — bright pass, six-level bloom
 *     pyramid, halation, exposure, aperture grille, scanlines, vignette, grain, the
 *     AMD-3 hue-preserving tonemap, sRGB. Not a "CRT-ish" filter; the same arithmetic
 *     in the same order.
 *  3. **No colour of its own.** Every pixel is a ramp index resolved through
 *     `src/render/palette.ts`, so a colourblind preset remaps the face along with the
 *     rest of the game and §11.14 holds by construction.
 *  4. **Nothing per frame.** Composition is per captain. `composePortrait` allocates its
 *     two buffers and the tube's scratch pyramid once; `recompose` reuses them. There is
 *     no update, no tick and no rAF anywhere in this module.
 *
 * On size. The art pipeline's measurement was blunt: at 1× (64 CSS px) a portrait is two
 * smudges and carries no identity. That is a fact about 64 px, not about this generator,
 * and it decides where the portrait can be spent. Use `HUD_SCALE` for presence — the
 * player should know somebody is in there — and `CARD_SCALE` for the death card, which
 * is the one place the face has to be a person you could pick out of a line-up.
 */

import { LINEAR, TOKENS, resolveToken, tokenIndex } from '../render/palette';
import { CANVAS, RAMP_TOKENS, RAMP_SIZE } from './anchors';
import { paintCaptain } from './parts';
import type { Captain, Appearance } from './seed';
import { applyTube, type TubeOptions } from './crt';

export { CANVAS as PORTRAIT_SIZE } from './anchors';

/** Run HUD. Presence, not identity — see the note above. */
export const HUD_SCALE = 2;
/** Death card. 192 CSS px, which fits a 390 px phone viewport with room for the plate. */
export const CARD_SCALE = 3;

/** `pal.ui.bg` — ramp index 2. See `PortraitOptions.background`. */
const DEFAULT_BACKGROUND = 2;

export interface PortraitOptions extends Partial<TubeOptions> {
  /** Output pixels per source pixel. Integer. Defaults to `CARD_SCALE`. */
  scale?: number;
  /** Skip the tube. For tests and for the distinctness metric, never for display. */
  raw?: boolean;
  /**
   * Ramp index painted behind the face, or `null` for transparent.
   *
   * Defaults to `V.deep` (`pal.ui.bg`) and that default is the point: with a field
   * behind it the whole 64×64 becomes a small screen — the vignette darkens its
   * corners, the grille runs across it, the grain sits on it — instead of a face
   * cut out and floated over whatever the UI happens to be. Pass `null` if the panel
   * behind the portrait already carries the tube.
   */
  background?: number | null;
}

export interface Portrait {
  /** The run seed this face was derived from. */
  seed: number;
  scale: number;
  /** Display size in pixels, `CANVAS * scale`. */
  width: number;
  height: number;
  /** Native 64×64 ramp indices. 0 is transparent; 1…10 index `RAMP_TOKENS`. */
  indices: Uint8Array;
  /** Display RGBA, premultiplied by nothing, ready for `putImageData`. */
  pixels: Uint8ClampedArray;
}

// ---------------------------------------------------------------------------
// Ramp resolution
// ---------------------------------------------------------------------------

/** sRGB bytes and linear floats for the eleven ramp values, under the active preset. */
const rampRgb8 = new Uint8Array(RAMP_SIZE * 3);
const rampLinear = new Float32Array(RAMP_SIZE * 3);
let rampPresetKey = '';

/**
 * The palette module rebuilds `LINEAR` when the colourblind preset changes but does not
 * announce it, so the ramp is keyed on the resolved hexes themselves. Eleven string
 * compares per composition is nothing, and it means a preset change cannot leave a stale
 * face on screen.
 */
function syncRamp(): void {
  let key = '';
  for (let i = 1; i < RAMP_SIZE; i++) key += resolveToken(RAMP_TOKENS[i]);
  if (key === rampPresetKey) return;
  rampPresetKey = key;
  for (let i = 1; i < RAMP_SIZE; i++) {
    const hex = resolveToken(RAMP_TOKENS[i]);
    const n = parseInt(hex.slice(1), 16);
    rampRgb8[i * 3] = (n >> 16) & 0xff;
    rampRgb8[i * 3 + 1] = (n >> 8) & 0xff;
    rampRgb8[i * 3 + 2] = n & 0xff;
    const li = tokenIndex(RAMP_TOKENS[i]) * 3;
    rampLinear[i * 3] = LINEAR[li];
    rampLinear[i * 3 + 1] = LINEAR[li + 1];
    rampLinear[i * 3 + 2] = LINEAR[li + 2];
  }
}

/** Every ramp token is a real palette key. Throws at boot rather than drawing black. */
export function assertRampIsPalette(): void {
  for (let i = 1; i < RAMP_SIZE; i++) {
    if (!TOKENS.includes(RAMP_TOKENS[i])) throw new Error(`portrait: "${RAMP_TOKENS[i]}" is not a palette token`);
  }
}

// ---------------------------------------------------------------------------
// Composition
// ---------------------------------------------------------------------------

const linearScratch = new Float32Array(CANVAS * CANVAS * 3);
const alphaScratch = new Float32Array(CANVAS * CANVAS);

/** Native indices → linear RGB + coverage, the two planes the tube reads. */
function expand(indices: Uint8Array, background: number | null): void {
  syncRamp();
  for (let i = 0; i < CANVAS * CANVAS; i++) {
    const v = indices[i] === 0 && background !== null ? background : indices[i];
    const o = i * 3;
    if (v === 0) {
      linearScratch[o] = 0;
      linearScratch[o + 1] = 0;
      linearScratch[o + 2] = 0;
      alphaScratch[i] = 0;
      continue;
    }
    linearScratch[o] = rampLinear[v * 3];
    linearScratch[o + 1] = rampLinear[v * 3 + 1];
    linearScratch[o + 2] = rampLinear[v * 3 + 2];
    alphaScratch[i] = 1;
  }
}

/** Nearest-neighbour blit of the native ramp, no tube. Palette-exact by construction. */
function blitRaw(indices: Uint8Array, out: Uint8ClampedArray, scale: number, background: number | null): void {
  syncRamp();
  const outW = CANVAS * scale;
  for (let y = 0; y < CANVAS; y++) {
    for (let x = 0; x < CANVAS; x++) {
      const raw = indices[y * CANVAS + x];
      const v = raw === 0 && background !== null ? background : raw;
      const r = rampRgb8[v * 3];
      const g = rampRgb8[v * 3 + 1];
      const b = rampRgb8[v * 3 + 2];
      const a = v === 0 ? 0 : 255;
      for (let sy = 0; sy < scale; sy++) {
        for (let sx = 0; sx < scale; sx++) {
          const o = ((y * scale + sy) * outW + x * scale + sx) * 4;
          out[o] = r;
          out[o + 1] = g;
          out[o + 2] = b;
          out[o + 3] = a;
        }
      }
    }
  }
}

/**
 * The whole job: a captain in, a blittable portrait out.
 *
 * Call it once when the run starts and hold the result. Calling it every frame would
 * work and would also be the single most expensive thing in the frame, which is why the
 * returned object is a plain buffer and not a live view.
 */
export function composePortrait(captain: Captain, opts: PortraitOptions = {}): Portrait {
  const scale = Math.max(1, Math.round(opts.scale ?? CARD_SCALE));
  const p: Portrait = {
    seed: captain.runSeed,
    scale,
    width: CANVAS * scale,
    height: CANVAS * scale,
    indices: new Uint8Array(CANVAS * CANVAS),
    pixels: new Uint8ClampedArray(CANVAS * scale * CANVAS * scale * 4),
  };
  recompose(captain, p, opts);
  return p;
}

/**
 * Repaints an existing portrait in place. Allocates nothing. Use it when the
 * colourblind preset changes, or to keep one buffer for a whole session of runs.
 */
export function recompose(captain: Captain, into: Portrait, opts: PortraitOptions = {}): void {
  const scale = into.scale;
  const background = opts.background === undefined ? DEFAULT_BACKGROUND : opts.background;
  paintCaptain(captain.appearance, into.indices);
  into.seed = captain.runSeed;
  if (opts.raw === true) {
    blitRaw(into.indices, into.pixels, scale, background);
    return;
  }
  expand(into.indices, background);
  applyTube(linearScratch, alphaScratch, CANVAS, CANVAS, into.pixels, {
    scale,
    scanline: opts.scanline,
    grain: opts.grain,
    glowSpill: opts.glowSpill,
  });
}

/** Just the native ramp indices, for tests, tooling and the distinctness metric. */
export function portraitIndices(appearance: Appearance, out: Uint8Array): void {
  paintCaptain(appearance, out);
}

// ---------------------------------------------------------------------------
// Hand-off to the DOM
// ---------------------------------------------------------------------------

/**
 * Writes the portrait into a canvas, sizing it to match. The canvas must be scaled with
 * CSS, never with a non-integer `width`, or the nearest-neighbour discipline the whole
 * anchor contract rests on is thrown away by the browser's resampler.
 *
 * Kept separate from composition so the compositor stays testable in `node`.
 */
export function paintPortraitToCanvas(p: Portrait, canvas: HTMLCanvasElement): boolean {
  const ctx = canvas.getContext('2d');
  if (ctx === null) return false;
  canvas.width = p.width;
  canvas.height = p.height;
  ctx.imageSmoothingEnabled = false;
  const img = ctx.createImageData(p.width, p.height);
  img.data.set(p.pixels);
  ctx.putImageData(img, 0, 0);
  return true;
}
